import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Ship, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Help", path: "/help" },
    { name: "Trading", path: "/trading" },
    { name: "AI Video", path: "/ai-video" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className="fixed top-0 w-full z-50 glass-header border-b border-white/5"
      data-testid="navigation-header"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
              <span className="text-black font-bold text-xl font-serif">G</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-lg font-semibold tracking-tight">Goldspot</span>
              <span className="text-xs uppercase tracking-widest text-muted-foreground block">Tax Service</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm uppercase tracking-widest whitespace-nowrap transition-colors duration-300 ${
                  isActive(link.path) 
                    ? "text-[#D4AF37]" 
                    : "text-muted-foreground hover:text-[#D4AF37]"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="tel:+12027042459" 
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#D4AF37] transition-colors"
              data-testid="phone-link"
            >
              <Phone size={16} />
              <span>(202) 704-2459</span>
            </a>
            <a
              href="https://wa.me/12027042459"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-muted-foreground hover:text-[#25D366] transition-colors"
              title="Chat on WhatsApp"
              data-testid="whatsapp-link"
            >
              <MessageCircle size={18} />
            </a>
            <a
              href="https://t.me/+12027042459"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-muted-foreground hover:text-[#24A1DE] transition-colors"
              title="Chat on Telegram"
              data-testid="telegram-link"
            >
              <Send size={18} />
            </a>
            <a href="tel:+12027042459">
              <Button 
                className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold px-6 rounded-sm"
                data-testid="call-now-btn"
              >
                Call Now
              </Button>
            </a>
            <Link 
              to="/trade" 
              className="text-xs text-muted-foreground/40 hover:text-[#D4AF37] transition-colors ml-2"
              title="Trade Admin"
              data-testid="trade-admin-link"
            >
              <Ship size={14} />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-[#D4AF37] transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="md:hidden py-6 border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm uppercase tracking-widest py-2 transition-colors duration-300 ${
                    isActive(link.path) 
                      ? "text-[#D4AF37]" 
                      : "text-muted-foreground hover:text-[#D4AF37]"
                  }`}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <Button 
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold mt-4 rounded-sm"
                  data-testid="mobile-call-btn"
                >
                  Contact Us
                </Button>
              </Link>
              <div className="flex justify-center gap-8 pt-2">
                <a
                  href="https://wa.me/12027042459"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#25D366] transition-colors"
                  data-testid="mobile-whatsapp-link"
                >
                  <MessageCircle size={18} /> WhatsApp
                </a>
                <a
                  href="https://t.me/+12027042459"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#24A1DE] transition-colors"
                  data-testid="mobile-telegram-link"
                >
                  <Send size={18} /> Telegram
                </a>
              </div>
              <Link 
                to="/trade" 
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 text-xs text-muted-foreground/60 hover:text-[#D4AF37] transition-colors pt-4 border-t border-white/5 mt-2"
                data-testid="mobile-trade-link"
              >
                <Ship size={14} /> Trade Admin
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navigation;
